<template>
  <v-container fluid>

    <!-- Header -->
    <v-row align="center" class="mb-4">
      <v-col>
        <div class="d-flex align-center">
          <v-icon size="28" color="primary" class="mr-3">mdi-table-chair</v-icon>
          <div>
            <h2 class="text-h5 font-weight-bold">{{ $t('ticket_name.title.main') }}</h2>
            <p class="body-2 grey--text mb-0">{{ $t('ticket_name.title.subtitle') }}</p>
          </div>
        </div>
      </v-col>
      <v-col class="text-right">
        <v-btn color="primary" v-can="'ticket_name.create'" @click="openCreateDialog">
          <v-icon left>mdi-plus</v-icon>
          {{ $t('ticket_name.button.create') }}
        </v-btn>
      </v-col>
    </v-row>

    <!-- Store filter -->
    <v-card outlined class="mb-4">
      <v-card-text class="py-3">
        <v-row dense align="center">
          <v-col cols="12" sm="6" md="4">
            <v-select
              v-model="selectedStoreId"
              :items="stores"
              item-text="name"
              item-value="id"
              :label="$t('ticket_name.label.select_store')"
              outlined
              dense
              clearable
              hide-details
              @change="fetchNames"
            />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Table -->
    <v-card outlined>
      <v-data-table
        :headers="headers"
        :items="names"
        :loading="loading"
        :footer-props="{ itemsPerPageOptions: [15, 30, 50, 100] }"
        :items-per-page="15"
        sort-by="sort_order"
      >
        <template #[`item.group_name`]="{ item }">
          <v-chip v-if="item.group_name" small color="blue-grey lighten-4" text-color="blue-grey darken-2">
            {{ item.group_name }}
          </v-chip>
          <span v-else class="grey--text">—</span>
        </template>

        <template #[`item.store`]="{ item }">
          {{ item.store ? item.store.name : '—' }}
        </template>

        <template #[`item.is_active`]="{ item }">
          <v-chip small :color="item.is_active ? 'success' : 'error'" dark>
            {{ item.is_active ? $t('label.active') : $t('label.inactive') }}
          </v-chip>
        </template>

        <template #[`item.open_tickets_count`]="{ item }">
          <v-badge
            v-if="item.open_tickets_count > 0"
            :content="String(item.open_tickets_count)"
            color="warning"
            inline
          >
            <v-icon small>mdi-ticket-confirmation</v-icon>
          </v-badge>
          <span v-else class="grey--text">0</span>
        </template>

        <template #[`item.actions`]="{ item }">
          <v-btn
            small
            icon
            v-can.disable="'ticket_name.update'"
            @click="openEditDialog(item)"
          >
            <v-icon small>mdi-pencil</v-icon>
          </v-btn>
          <v-menu offset-y left>
            <template #activator="{ on }">
              <v-btn small icon v-on="on">
                <v-icon small>mdi-dots-vertical</v-icon>
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item v-can.disable="'ticket_name.update'" @click="toggleActive(item)">
                <v-list-item-icon>
                  <v-icon small>{{ item.is_active ? 'mdi-archive' : 'mdi-archive-arrow-up-outline' }}</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>
                    {{ item.is_active ? $t('button.deactivate') : $t('button.activate') }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item v-can.disable="'ticket_name.delete'" @click="confirmDelete(item)">
                <v-list-item-icon>
                  <v-icon small color="error">mdi-trash-can-outline</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title class="error--text">{{ $t('button.delete') }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>
      </v-data-table>
    </v-card>

    <!-- Create / Edit Dialog -->
    <create-edit-ticket-name
      :display="showDialog"
      :ticket_name="selectedName"
      :stores="stores"
      @close="closeDialog"
      @saved="onSaved"
    />

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="420" persistent>
      <v-card>
        <v-card-title>{{ $t('button.delete') }}</v-card-title>
        <v-card-text>
          <v-alert type="error" outlined dense class="mb-0">
            {{ $t('ticket_name.confirm.delete') }}
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="deleteDialog = false">{{ $t('button.cancel') }}</v-btn>
          <v-btn color="error" dark :loading="deleteLoading" @click="deleteName">
            {{ $t('button.delete') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" top right timeout="4000">
      {{ snackbar.message }}
      <template #action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">{{ $t('button.close') }}</v-btn>
      </template>
    </v-snackbar>

  </v-container>
</template>

<script>
import CreateEditTicketName from './Modal/CreateEdit'

export default {
  name: 'TicketNamesIndex',
  components: { CreateEditTicketName },

  data () {
    return {
      names: [],
      stores: [],
      selectedStoreId: null,
      loading: false,
      showDialog: false,
      selectedName: {},
      deleteDialog: false,
      deleteTarget: null,
      deleteLoading: false,
      snackbar: { show: false, message: '', color: 'success' },
      headers: [
        { text: this.$t('label.name'), value: 'name', sortable: true },
        { text: this.$t('ticket_name.label.group'), value: 'group_name', sortable: true },
        { text: this.$t('label.store'), value: 'store', sortable: false },
        { text: this.$t('ticket_name.label.sort_order'), value: 'sort_order', sortable: true, align: 'center', width: 110 },
        { text: this.$t('label.status'), value: 'is_active', sortable: true, align: 'center', width: 110 },
        { text: this.$t('ticket_name.label.open_tickets'), value: 'open_tickets_count', sortable: true, align: 'center', width: 120 },
        { text: '', value: 'actions', sortable: false, align: 'right', width: 90 }
      ]
    }
  },

  methods: {
    async fetchStores () {
      try {
        this.stores = await this.$departmentService.getDepartments()
      } catch (e) {
        console.error('fetchStores', e)
      }
    },

    async fetchNames () {
      this.loading = true
      try {
        const params = {}
        if (this.selectedStoreId) params.store_id = this.selectedStoreId
        const resp = await this.$ticketNameService.getAll(params)
        this.names = (resp.data || []).map(n => ({
          ...n,
          store: this.stores.find(s => s.id === n.store_id) || null
        }))
      } catch (e) {
        this.showSnackbar('Failed to load names.', 'error')
      } finally {
        this.loading = false
      }
    },

    openCreateDialog () {
      this.selectedName = {}
      this.showDialog = true
    },

    openEditDialog (row) {
      this.selectedName = { ...row }
      this.showDialog = true
    },

    closeDialog () {
      this.showDialog = false
      this.selectedName = {}
    },

    onSaved (data) {
      const isEdit = !!this.selectedName.id
      const items = Array.isArray(data) ? data : [data]

      if (isEdit) {
        const idx = this.names.findIndex(n => n.id === items[0].id)
        if (idx !== -1) {
          this.names.splice(idx, 1, {
            ...items[0],
            store: this.stores.find(s => s.id === items[0].store_id) || null
          })
        }
        this.showSnackbar(this.$t('ticket_name.message.updated'), 'success')
      } else {
        items.forEach(item => {
          if (!this.selectedStoreId || item.store_id === this.selectedStoreId) {
            this.names.push({
              ...item,
              open_tickets_count: 0,
              store: this.stores.find(s => s.id === item.store_id) || null
            })
          }
        })
        this.showSnackbar(this.$t('ticket_name.message.created'), 'success')
      }

      this.closeDialog()
    },

    async toggleActive (row) {
      try {
        const data = await this.$ticketNameService.update(row.id, { ...row, is_active: !row.is_active })
        const idx = this.names.findIndex(n => n.id === row.id)
        if (idx !== -1) {
          this.names.splice(idx, 1, { ...data, store: this.stores.find(s => s.id === data.store_id) || null })
        }
        this.showSnackbar(this.$t('ticket_name.message.updated'), 'success')
      } catch (e) {
        this.showSnackbar('Update failed.', 'error')
      }
    },

    confirmDelete (row) {
      this.deleteTarget = row
      this.deleteDialog = true
    },

    async deleteName () {
      if (!this.deleteTarget) return
      this.deleteLoading = true
      try {
        await this.$ticketNameService.destroy(this.deleteTarget.id)
        this.names = this.names.filter(n => n.id !== this.deleteTarget.id)
        this.showSnackbar(this.$t('ticket_name.message.deleted'), 'success')
        this.deleteDialog = false
        this.deleteTarget = null
      } catch (e) {
        this.showSnackbar('Delete failed.', 'error')
      } finally {
        this.deleteLoading = false
      }
    },

    showSnackbar (message, color = 'success') {
      this.snackbar = { show: true, message, color }
    }
  },

  async mounted () {
    await this.fetchStores()
    await this.fetchNames()
  }
}
</script>
